import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../api/axiosConfig';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CreateMappingModal } from '../components/CreateMappingModal';
import { useWebSocket } from '../contexts/WebSocketContext';
import Pagination from '../components/Pagination';

interface ReportStatus {
  status: 'idle' | 'started' | 'in_progress' | 'completed' | 'error';
  progress: number;
  message: string;
  lastGenerated?: string;
}

interface ReportAvailability {
  available: boolean;
  status: string;
  lastGenerated?: string;
}

interface UnmappedItem {
  id: number;
  name: string;
  type: 'network' | 'group';
}

// Reusable button component to reduce duplication
interface ActionButtonProps {
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
}

const ActionButton: React.FC<ActionButtonProps> = ({ 
  onClick, 
  disabled, 
  icon, 
  children, 
  variant = 'primary' 
}) => {
  const baseClasses = "inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2";
  const primaryClasses = "border-transparent bg-primary text-white hover:bg-primary-dark";
  const secondaryClasses = "border-gray-300 bg-white text-gray-700 hover:bg-gray-50";

  const classes = `${baseClasses} ${variant === 'primary' ? primaryClasses : secondaryClasses} ${
    disabled ? 'opacity-75 cursor-not-allowed' : ''
  }`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {icon}
      {children}
    </button>
  );
};

const ReportPage: React.FC = () => {
  // Use WebSocket context for real-time updates
  const { reportProgress } = useWebSocket();

  const [reportStatus, setReportStatus] = useState<ReportStatus>({
    status: 'idle',
    progress: 0,
    message: '',
  });
  const [reportAvailability, setReportAvailability] = useState<ReportAvailability>({
    available: false,
    status: 'idle',
  });
  const [mappingsUpdated, setMappingsUpdated] = useState(false);
  const [unmappedItems, setUnmappedItems] = useState<UnmappedItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UnmappedItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExcludingItem, setIsExcludingItem] = useState<number | null>(null);
  const [isForceRegenerating, setIsForceRegenerating] = useState(false);
  // Use refs to prevent duplicate API calls
  const statusRetrievedRef = useRef(false);
  const unmappedItemsRetrievedRef = useRef(false);
  const availabilityCheckedRef = useRef(false);

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'network' | 'group'>('all');

  // Helper function to check if report is in progress
  const isReportInProgress = (): boolean => {
    return reportStatus.status === 'started' || reportStatus.status === 'in_progress';
  };

  // Function to start report generation
  const generateReport = async (forceRegenerate: boolean = false) => {
    try {
      setIsLoading(true);

      // Call the API to start report generation
      await apiClient.post(`/api/reports/generate${forceRegenerate ? '?force=true' : ''}`);

      // Report status will be updated via WebSocket
    } catch (error) {
      console.error('Failed to start report generation:', error);
      setReportStatus({
        status: 'error',
        progress: 0,
        message: 'Failed to start report generation',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download report
  const downloadReport = async (forceRegenerate: boolean = false) => {
    try {
      setIsLoading(true);

      // If we're force regenerating, reset the mappingsUpdated state immediately
      // and set isForceRegenerating to true
      if (forceRegenerate) {
        setMappingsUpdated(false);
        setIsForceRegenerating(true);
        setCurrentPage(1)
      }

      // Use apiClient to make an authenticated request to the download endpoint
      const response = await apiClient.get(`/api/reports/download${forceRegenerate ? '?force=true' : ''}`, {
        responseType: 'blob', // Important for handling binary data
      });

      // Create a blob URL from the response data
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      const url = window.URL.createObjectURL(blob);

      // Create a link element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'annual-report.xlsx');
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // If we're forcing regeneration, we need to wait for the report to be generated
      if (forceRegenerate) {
        // Report status will be updated via WebSocket
      } else {
        // If we're just downloading an existing report, fetch unmapped items
        fetchUnmappedItems();
      }
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a report is available
  const checkReportAvailability = async (forceCheck: boolean = false) => {
    try {
      // Check if we haven't already checked availability or if we're forcing a check
      if (!availabilityCheckedRef.current || forceCheck) {
        const response = await apiClient.get('/api/reports/available');
        setReportAvailability(response.data);

        // Check if mappings have been updated since the last report generation
        if (response.data.mappingsUpdated) {
          setMappingsUpdated(true);
        } else {
          setMappingsUpdated(false);
        }

        // Mark that we've checked availability
        availabilityCheckedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to check report availability:', error);
    }
  };

  // Update report status from WebSocket
  useEffect(() => {
    if (reportProgress) {
      setReportStatus({
        status: reportProgress.status as any,
        progress: reportProgress.progress,
        message: reportProgress.message,
      });

      // If the report is completed, fetch unmapped items
      if (reportProgress.status === 'completed') {
        fetchUnmappedItems();
        checkReportAvailability(true); // Force check availability to update UI state
        // Reset isForceRegenerating when report generation is completed
        setIsForceRegenerating(false);
      }
    }
  }, [reportProgress]);

  // Initial load - check report status and availability
  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        // Get current report status
        const statusResponse = await apiClient.get('/api/reports/status');
        setReportStatus(statusResponse.data);
        statusRetrievedRef.current = true;

        // Check if a report is available
        await checkReportAvailability();

        // If the report is completed, fetch unmapped items
        if (statusResponse.data.status === 'completed') {
          fetchUnmappedItems();
        }
      } catch (error) {
        console.error('Failed to fetch initial status:', error);
        statusRetrievedRef.current = true; // Even if there's an error, we've attempted to retrieve the status
      }
    };

    fetchInitialStatus();
  }, []);

  // Fetch unmapped items when the report is completed
  const fetchUnmappedItems = async () => {
    try {
      // Fetch unmapped entities (networks and groups)
      const response = await apiClient.get('/api/reports/unmapped-entities');

      // Process networks
      const networks = response.data.networks.map((item: any) => ({
        ...item,
        type: 'network' as const,
      }));

      // Process groups
      const groups = response.data.groups.map((item: any) => ({
        ...item,
        type: 'group' as const,
      }));

      // Combine and set unmapped items
      setUnmappedItems([...networks, ...groups]);

      // Mark that we've retrieved unmapped items
      unmappedItemsRetrievedRef.current = true;
    } catch (error) {
      console.error('Failed to fetch unmapped items:', error);
    }
  };

  // Handle adding to excluded entities
  const handleAddToExcluded = async (item: UnmappedItem) => {
    try {
      // Set the loading state for this specific item
      setIsExcludingItem(item.id);

      // Ensure the item has a name
      if (!item.name) {
        console.error('Cannot add to excluded entities: Item name is required');
        return;
      }

      // Call the appropriate API endpoint based on the item type
      if (item.type === 'network') {
        await apiClient.post('/api/excluded-entities/networks/add', { 
          networkId: item.id,
          name: item.name
        });
      } else {
        await apiClient.post('/api/excluded-entities/groups/add', { 
          groupId: item.id,
          name: item.name
        });
      }

      // Remove the item from the unmapped items list
      setUnmappedItems(unmappedItems.filter((i) => !(i.id === item.id && i.type === item.type)));

      // Set mappingsUpdated to true to show the warning message
      setMappingsUpdated(true);
    } catch (error) {
      console.error('Failed to add to excluded entities:', error);
    } finally {
      // Clear the loading state
      setIsExcludingItem(null);
    }
  };

  // Handle creating a mapping
  const handleCreateMapping = (item: UnmappedItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = (mappingCreated: boolean) => {
    setIsModalOpen(false);
    setSelectedItem(null);

    // If a mapping was created, refresh the unmapped items list
    if (mappingCreated) {
      fetchUnmappedItems();
      // Set mappingsUpdated to true to show the warning message
      setMappingsUpdated(true);
    }
  };

  // Filter unmapped items based on search term and filter type
  const filteredItems = unmappedItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || item.type === filterType;
    return matchesSearch && matchesType;
  });

  // Get current items for the current page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Handle search term change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle filter type change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(e.target.value as 'all' | 'network' | 'group');
    setCurrentPage(1); // Reset to first page when filter changes
  };

  return (
    <div className="space-y-8">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Annual Report
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Generate a report of user data by country and year. This process may take a few minutes.
            </p>
            {reportAvailability.lastGenerated && (
              <p className="mt-2">
                <span className="font-medium">Last generated:</span>{' '}
                {new Date(reportAvailability.lastGenerated).toLocaleString()}
              </p>
            )}
            {mappingsUpdated && (
              <p className="mt-2 text-red-600 font-medium">
                Mappings have been updated since the last report generation. Please regenerate the report for the latest data.
              </p>
            )}
          </div>
          <div className="mt-5 flex space-x-4">
            {reportAvailability.available ? (
              <>
                <ActionButton
                  onClick={() => downloadReport(false)}
                  disabled={isLoading || !unmappedItemsRetrievedRef.current}
                  icon={<ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
                  variant="primary"
                >
                  Download Report
                </ActionButton>
                <ActionButton
                  onClick={() => downloadReport(true)}
                  disabled={isLoading || isReportInProgress() || !unmappedItemsRetrievedRef.current}
                  icon={<ArrowPathIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
                  variant="secondary"
                >
                  Force Regenerate
                </ActionButton>
              </>
            ) : (
              <ActionButton
                onClick={() => generateReport(false)}
                disabled={!statusRetrievedRef.current || isLoading || isReportInProgress()}
                icon={<ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
                variant="primary"
              >
                Generate Report
              </ActionButton>
            )}
          </div>
        </div>
      </div>

      {/* Progress section */}
      {(reportStatus.status === 'started' || reportStatus.status === 'in_progress') && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Report Generation Progress
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">{reportStatus.message}</p>
              <p className="mt-2 text-sm text-gray-500 italic">This process may take a while. You can leave this page and come back later to check the progress.</p>
            </div>
            <div className="mt-4">
              <div className="relative h-4 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="absolute h-full bg-primary transition-all duration-300 ease-in-out"
                  style={{ width: `${reportStatus.progress}%` }}
                ></div>
              </div>
              <p className="mt-2 text-sm text-gray-500 text-right">
                {reportStatus.progress}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {reportStatus.status === 'error' && (
        <div className="rounded-md bg-boundlessBlue p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-white">
                Error generating report
              </h3>
              <div className="mt-2 text-sm text-white">
                <p>{reportStatus.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unmapped items table - hide when force regenerating */}
      {unmappedItems.length > 0 && !isForceRegenerating && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Unmapped Entities
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>
                The following entities need to be mapped to countries or excluded.
              </p>
            </div>

            {/* Search and filter controls */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex-1">
                <label htmlFor="search" className="sr-only">
                  Search
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="search"
                    id="search"
                    className="block w-full rounded-md border-gray-300 pr-10 focus:border-primary focus:ring-primary sm:text-sm"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <label htmlFor="filter" className="sr-only">
                  Filter by type
                </label>
                <select
                  id="filter"
                  name="filter"
                  className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                  value={filterType}
                  onChange={handleFilterChange}
                >
                  <option value="all">All Types</option>
                  <option value="network">Networks</option>
                  <option value="group">Groups</option>
                </select>
              </div>
            </div>

            <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {currentItems.map((item) => (
                    <tr key={`${item.type}-${item.id}`}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {item.id}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {item.type === 'network' ? 'Network' : 'Group'}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <button
                          type="button"
                          onClick={() => handleAddToExcluded(item)}
                          disabled={isExcludingItem === item.id}
                          className={`text-accent hover:text-accent-dark mr-4 ${isExcludingItem === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isExcludingItem === item.id ? 'Adding...' : 'Add to Excluded'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCreateMapping(item)}
                          className="text-primary hover:text-primary-dark"
                        >
                          Create Mapping
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredItems.length > itemsPerPage && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredItems.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={paginate}
                />
              </div>
            )}

            {/* Show message when no results match filter */}
            {filteredItems.length === 0 && (
              <div className="mt-4 text-center py-4 text-gray-500">
                No items match your search criteria.
              </div>
            )}
            <div className="mt-6">
              <ActionButton
                onClick={() => downloadReport(true)}
                disabled={isLoading || isReportInProgress() || !unmappedItemsRetrievedRef.current}
                icon={<ArrowPathIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
                variant="primary"
              >
                Regenerate Report
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Mapping modal */}
      {isModalOpen && selectedItem && (
        <CreateMappingModal
          item={selectedItem}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default ReportPage;
